import { Alert, Button } from '@mui/material';
import { FileSignature } from 'lucide-react';
import { useContext, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { QueryClientContext, useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePatientTenant } from '../../contexts/PatientTenantContext';
import { usePatientDependents } from '../../contexts/PatientDependentContext';
import { patientProcedureConsentsModule } from '../../lib/api/modules/consentForms';

/** Same key as the "Forms to sign" page, so signing there updates the badge and the home card. */
export const PATIENT_CONSENTS_KEY = ['patient-procedure-consents'];

/** The list's key for the clinic and family member being viewed (switching either shows that record's forms). */
export function usePatientConsentsListKey(): unknown[] {
  const { activeClinic } = usePatientTenant();
  const { activeDependent } = usePatientDependents();
  return [...PATIENT_CONSENTS_KEY, 'list', activeClinic?.clinicId ?? null, activeDependent?.dependentPatientId ?? null];
}

/**
 * Forms the clinic sent to the patient's account that still wait for a signature. Light on purpose: the portal's
 * sidebar badge and the home card use it on every page.
 */
export function usePatientFormsWaiting(enabled = true): number {
  return usePatientFormsWaitingState(enabled).count;
}

/** The count plus whether the list has loaded at least once (for {@link usePatientNewFormAlert}). */
export function usePatientFormsWaitingState(enabled = true): { count: number; loaded: boolean } {
  const queryKey = usePatientConsentsListKey();
  const list = useQuery({
    queryKey,
    queryFn: async () => { const r = await patientProcedureConsentsModule.list(); if (r.error) throw new Error(r.error); return r.data ?? []; },
    enabled,
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  });
  return { count: (list.data ?? []).filter((c) => c.status === 'PENDING').length, loaded: list.isSuccess };
}

/**
 * The portal's notification for a new form (the web counterpart of the app's push): when the waiting count goes up
 * while the patient is signed in — the clinic just sent one — a toast says so, with Open. Not on the first load (the
 * badge and the home card already show what was waiting) and not on the Forms to sign page itself. Starts over when the
 * patient switches clinic or family member. Used once, by the portal layout.
 */
export function usePatientNewFormAlert(count: number, loaded: boolean): void {
  const { t } = useTranslation('consentForms');
  const queryKey = JSON.stringify(usePatientConsentsListKey());
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const seen = useRef<{ key: string; count: number } | null>(null);
  useEffect(() => {
    if (!loaded) return;
    const prev = seen.current;
    seen.current = { key: queryKey, count };
    if (!prev || prev.key !== queryKey || count <= prev.count || pathname === '/patient/forms-to-sign') return;
    toast((tt) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>{t('portal.newFormToast', { count: count - prev.count })}</span>
        <Button size="small" variant="contained" onClick={() => { toast.dismiss(tt.id); navigate('/patient/forms-to-sign'); }}>
          {t('portal.open')}
        </Button>
      </span>
    ), { id: 'patient-new-form', icon: '✍️', duration: 10_000, ariaProps: { role: 'status', 'aria-live': 'polite' } });
  }, [count, loaded, queryKey, pathname, navigate, t]);
}

/** Home card: "You have a form to read and sign" + Open, only while one is waiting. */
export function PatientFormsWaitingCard() {
  // Renders nothing outside a query client (screens rendered on their own, e.g. in tests).
  return useContext(QueryClientContext) ? <FormsWaitingCard /> : null;
}

function FormsWaitingCard() {
  const { t } = useTranslation('consentForms');
  const count = usePatientFormsWaiting();
  if (count === 0) return null;
  return (
    <Alert severity="warning" icon={<FileSignature size={22} />} sx={{ mb: 2, alignItems: 'center', borderRadius: 2 }}
      action={<Button component={RouterLink} to="/patient/forms-to-sign" color="inherit" variant="outlined" size="small">{t('portal.open')}</Button>}>
      {t('portal.homeCard', { count })}
    </Alert>
  );
}
